/**
 * What to lift next.
 *
 * This is the highest-value feature in the app per line of code. The thing that
 * actually kills a training habit is standing at the bar deciding what to do;
 * pre-filling every field with last session's numbers and a concrete target
 * removes that decision entirely.
 *
 * The rule is double progression, which is what most sane intermediate
 * programmes use: work up the rep range at a fixed weight, and only when you
 * hit the top of the range on every working set — without grinding — add the
 * smallest increment and drop back to the bottom of the range.
 *
 * On an assist machine the increment is SUBTRACTED. The stack takes weight off,
 * so `assisted: true` inverts every direction in here: the heaviest set is the
 * one with the least help, and answering a cleared rep range by adding 5 kg
 * meant the app's single most-used piece of advice was "make it easier" — which
 * it did, silently, to the one exercise a beginner is most likely to be on.
 */

import type { SetRow } from '../db'
import { roundToGymStep, type Unit } from './units.ts'

export type Suggestion = {
  weightKg: number | null
  reps: number | null
  /** Shown to the user. Being able to see *why* is what makes it trustworthy. */
  reason: string
  kind: 'increase' | 'repeat' | 'hold' | 'first-time'
}

export type ProgressionOpts = {
  repsLow?: number
  repsHigh?: number
  /** Above this, the set was a grind and doesn't count as clearing the range. */
  rpeCeiling?: number
  incrementKg?: number
  /**
   * The unit the person lifts in, because a gym's steps are its own.
   *
   * A pounds gym moves in fives — 45s, 25s, 10s, and 2.5s that come in pairs —
   * and a stack's pins are its own spacing. This used to snap every suggestion
   * to 2.5 kg barbell plate PAIRS regardless, so a 135 lb bench came back as
   * 137.8 lb and a 120 lb pulldown as 132.3 lb: numbers no bar and no machine
   * can be set to. Nothing rendered `weightKg`, which is the only reason it was
   * never noticed.
   */
  unit?: Unit
  /** Unused now that rounding goes through the increment; kept so existing
   *  callers don't break. */
  barKg?: number
  /**
   * The weight is assistance, so progress runs downward. Everything the rule
   * calls "heaviest", "add" and "the same weight" flips with it.
   */
  assisted?: boolean
}

export function suggestNext(
  lastSets: SetRow[] | null,
  opts: ProgressionOpts = {},
): Suggestion {
  const repsLow = opts.repsLow ?? 5
  const repsHigh = opts.repsHigh ?? 8
  const rpeCeiling = opts.rpeCeiling ?? 8.5
  const increment = opts.incrementKg ?? 2.5
  const assisted = opts.assisted ?? false
  const unit = opts.unit ?? 'kg'

  const working = (lastSets ?? []).filter((s) => !s.is_warmup && s.reps != null)

  if (working.length === 0) {
    return {
      weightKg: null,
      reps: repsLow,
      reason: assisted
        ? 'First time — start with enough help to finish every rep, then take it off.'
        : 'First time — pick a weight you could do a couple more reps with.',
      kind: 'first-time',
    }
  }

  // Use the hardest working set as the reference. Back-off sets are common and
  // shouldn't drag the suggestion down — which on an assist machine means the
  // set with the LEAST help, not the largest number.
  const weights = working.map((s) => s.weight_kg ?? 0)
  const topWeight = assisted ? Math.min(...weights) : Math.max(...weights)
  const atTop = working.filter((s) => (s.weight_kg ?? 0) === topWeight)

  const clearedRange = atTop.every((s) => (s.reps ?? 0) >= repsHigh)
  const anyFailure = atTop.some((s) => s.is_failure)
  const ground = atTop.some((s) => s.rpe != null && s.rpe > rpeCeiling)

  if (clearedRange && !anyFailure && !ground) {
    // `roundToGymStep` and not `roundToLoadable`: the first knows the unit and
    // the exercise's own step, the second snaps to metric barbell plate pairs
    // whatever you hand it. Zero is kept as zero rather than rounded up — at no
    // assistance the progression has arrived at the real movement, and a
    // negative stack setting does not exist.
    const raw = assisted ? topWeight - increment : topWeight + increment
    const next = raw <= 0 ? 0 : roundToGymStep(raw, unit, increment)
    return {
      weightKg: next,
      reps: repsLow,
      reason: assisted
        ? next === 0
          ? `Hit ${repsHigh} on every set with the last of the help — try it unassisted.`
          : `Hit ${repsHigh} on every set last time — take help off and reset to ${repsLow}.`
        : `Hit ${repsHigh} on every set last time — add weight and reset to ${repsLow}.`,
      kind: 'increase',
    }
  }

  if (anyFailure || ground) {
    const bestReps = Math.max(...atTop.map((s) => s.reps ?? 0))
    return {
      weightKg: topWeight,
      reps: Math.max(repsLow, bestReps),
      reason: assisted
        ? ground
          ? 'Last session was a grind — keep the same help before taking more off.'
          : 'Missed reps last time — repeat before taking help off.'
        : ground
          ? 'Last session was a grind — repeat the same weight before adding.'
          : 'Missed reps last time — repeat before adding weight.',
      kind: 'hold',
    }
  }

  const bestReps = Math.max(...atTop.map((s) => s.reps ?? 0))
  const target = Math.min(repsHigh, bestReps + 1)
  return {
    weightKg: topWeight,
    reps: target,
    reason: `Same ${assisted ? 'help' : 'weight'}, aim for ${target} rep${
      target === 1 ? '' : 's'
    }.`,
    kind: 'repeat',
  }
}

/**
 * "3 × 8 @ 100 kg" style summary of a previous performance.
 *
 * Shows what was dialled in, not what it moved — this line exists to be copied
 * onto a machine. On an assist stack that number is help, so it says so: "@ 55
 * kg help" reads as a setting, where a bare "@ 55 kg" reads as a lift.
 */
export function describeSets(
  sets: SetRow[],
  format: (kg: number | null) => string,
  assisted = false,
): string {
  const working = sets.filter((s) => !s.is_warmup)
  if (working.length === 0) return '—'

  const suffix = assisted ? ' help' : ''
  const allSame =
    working.every((s) => s.reps === working[0].reps && s.weight_kg === working[0].weight_kg)

  if (allSame) {
    return `${working.length} × ${working[0].reps} @ ${
      format(working[0].weight_kg ?? null)}${suffix}`
  }
  return working
    .map((s) => `${s.reps}@${format(s.weight_kg ?? null)}${suffix}`)
    .join(', ')
}
